import {
  Box,
  Button,
  Flex,
  Text,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightElement,
  Link,
  Textarea,
  Checkbox,
  useToast,
  useDisclosure,
  useOutsideClick,
} from "@chakra-ui/react";

import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import React, { useState, useCallback, useRef } from "react";
import { SEO } from '@/components/seo';

import './landingPage.css';

const SITE_URL = import.meta.env.VITE_PWA_URL || '';

const LANDING_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Paire',
  url: SITE_URL || undefined,
  description: 'Discover personalized restaurant recommendations and share your favorite spots with friends on Paire.',
};

import { useNavigate, useSearch } from '@tanstack/react-router';
import { useLogin, useRegister, useForgotPassword, useResetPassword } from '@/api';
import { useLocationAutocomplete, SerializedSuggestion } from '@/api/queries/useAutocompleteQueries';
import { IntroCarousel } from '@/pages/intro/IntroCarousel';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

type PageState = 'intro' | 'login' | 'register' | 'reset';

/**
 * onMouseDown handler for the show/hide password toggles. Cancelling the
 * default action stops the tap/click from moving focus onto the button, so the
 * password input keeps focus (and the mobile keyboard stays up). Keyboard
 * activation (Tab to the button, Enter/Space) doesn't fire mousedown, so the
 * toggle stays fully keyboard-operable.
 */
const keepInputFocus = (e: React.MouseEvent) => e.preventDefault();

const Landing = () => {
  const navigate = useNavigate();
  // Single useSearch call — TanStack Router expects one per route component.
  // `code` carries the reset token formatted as "uid@token" from the email link.
  const { form, code } = useSearch({ from: '/login' });
  const { mutate: login } = useLogin();
  const { mutate: register, isPending: isRegistering } = useRegister();
  const { mutate: forgotPassword, isPending: isSendingReset } = useForgotPassword();
  const { mutate: resetPassword, isPending: isResettingPassword } = useResetPassword();

  // Parse uid and token from the code param (format: "uid@token").
  // Split on first "@" only — tokens may contain "@" themselves.
  const { uid: resetUid, token: resetToken } = (() => {
    if (!code) return { uid: '', token: '' };
    const atIndex = code.indexOf('@');
    if (atIndex === -1) return { uid: '', token: '' };
    return {
      uid: code.substring(0, atIndex),
      token: code.substring(atIndex + 1),
    };
  })();

  // Map form param to page state. When a valid reset code is present, go
  // directly to the reset form regardless of the `form` param.
  const initialPage: PageState = resetUid && resetToken
    ? 'reset'
    : form === 'signup' ? 'register' : form === 'login' ? 'login' : 'intro';
  const [currentPage, setCurrentPage] = useState<PageState>(initialPage);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  const [resetData, setResetData] = useState({
    newPassword1: '',
    newPassword2: '',
  });
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    location: "",
    dob: "",
    bio: "",
    password: "",
    confirmPassword: "",
    shareEmailOnProfile: false,
  });
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });
  const toast = useToast();

  // Location autocomplete
  const {
    suggestions: locationSuggestions,
    searchValue: locationSearchValue,
    setSearchValue: setLocationSearchValue,
    fetchSuggestions: fetchLocationSuggestions,
    selectSuggestion: selectLocationSuggestion,
  } = useLocationAutocomplete();

  // Dropdown visibility is tracked explicitly rather than derived from
  // `locationSuggestions.length > 0` — selecting a suggestion re-fires the
  // (now cache-cleared) autocomplete query for the selected text, which
  // Google Places happily returns matches for, so a suggestions-length-only
  // check never actually closes the dropdown.
  const {
    isOpen: isLocationSuggestionsOpen,
    onOpen: onLocationSuggestionsOpen,
    onClose: onLocationSuggestionsClose,
  } = useDisclosure();
  const locationFieldRef = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref: locationFieldRef, handler: onLocationSuggestionsClose });

  const handleLocationSelect = (suggestion: SerializedSuggestion) => {
    setFormData(prev => ({ ...prev, location: suggestion.displayText }));
    selectLocationSuggestion(suggestion);
    onLocationSuggestionsClose();
  };

  // Stable callback for location input - fixes Safari closure issue (PAIRE-APP-12)
  const handleLocationInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocationSearchValue(value);
      // Keep formData.location in sync with whatever the guest has typed —
      // handleLocationSelect will overwrite it with the suggestion's
      // canonical displayText if one is chosen, but free-typed text with no
      // dropdown selection must still be captured (GitHub #84).
      setFormData(prev => ({ ...prev, location: value }));
      fetchLocationSuggestions(value);
      onLocationSuggestionsOpen();
    },
    [setLocationSearchValue, fetchLocationSuggestions, onLocationSuggestionsOpen]
  );

  // Belt-and-suspenders close for keyboard tabbing away — useOutsideClick only
  // fires on pointer events, not focus changes. Delayed so a tap/click on a
  // suggestion (see onMouseDown + preventDefault below) isn't raced by blur.
  const handleLocationInputBlur = useCallback(() => {
    window.setTimeout(() => {
      onLocationSuggestionsClose();
    }, 150);
  }, [onLocationSuggestionsClose]);

  const handleBack = () => {
    setCurrentPage("intro");
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();

    // Track signup attempt
    analytics.capture(AnalyticsEvents.SIGNUP_CLICKED);

    // Validate required fields before submission
    if (!formData.firstName.trim()) {
      toast({
        title: "First name is required",
        status: "error",
        duration: 3000,
      });
      return;
    }
    if (!formData.lastName.trim()) {
      toast({
        title: "Last name is required",
        status: "error",
        duration: 3000,
      });
      return;
    }
    if (!formData.email.trim()) {
      toast({
        title: "Email is required",
        status: "error",
        duration: 3000,
      });
      return;
    }
    // The API rejects a blank location (User.location has no blank=True), so
    // City/State is required here (GitHub #84). Sits after email to follow the
    // visual field order.
    if (!formData.location.trim()) {
      toast({
        title: "City and state are required",
        status: "error",
        duration: 3000,
      });
      return;
    }
    if (!formData.dob) {
      toast({
        title: "Date of birth is required",
        status: "error",
        duration: 3000,
      });
      return;
    }
    if (!formData.password) {
      toast({
        title: "Password is required",
        status: "error",
        duration: 3000,
      });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Passwords do not match",
        status: "error",
        duration: 3000,
      });
      return;
    }
    
    register({ ...formData, location: formData.location.trim() }, {
      onSuccess: () => {
        toast({
          title: "Welcome to Paire!",
          status: "success",
          duration: 3000,
        });
        // User is now logged in, navigate to main app
        navigate({ to: '/search' });
      },
      onError: (error: any) => {
        const errorData = error?.response?.data;
        let errorMessage = "An error occurred";

        // Handle field-specific validation errors from the API
        if (errorData) {
          if (errorData.email) {
            // Check for unique constraint violation
            const emailErrors = Array.isArray(errorData.email) ? errorData.email : [errorData.email];
            if (emailErrors.some((e: string) => e.toLowerCase().includes('unique'))) {
              errorMessage = "This email is already registered. Please log in or use a different email.";
            } else {
              errorMessage = emailErrors.join(', ');
            }
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'object') {
            // Handle other field errors
            const firstError = Object.values(errorData)[0];
            errorMessage = Array.isArray(firstError) ? firstError.join(', ') : String(firstError);
          }
        } else if (error?.message) {
          errorMessage = error.message;
        }

        toast({
          title: "Registration failed",
          description: errorMessage,
          status: "error",
          duration: 5000,
        });
      },
    });
  };
  const handleLogin = async (e) => {
    e.preventDefault();

    // Track login attempt
    analytics.capture(AnalyticsEvents.LOGIN_CLICKED);

    login(
      { email: loginData.email, password: loginData.password },
      {
        onSuccess: () => {
          navigate({ to: '/search' });
        },
        onError: (error: any) => {
          toast({
            title: "Login failed",
            description: error?.data?.nonFieldErrors?.[0] || "An error occurred",
            status: "error",
            duration: 3000,
          });
        }
      }
    );
  };
  const handleForgotPassword = () => {
    // Guard: require an email before firing the mutation
    if (!loginData.email.trim()) {
      toast({
        title: "Email required",
        description: "Please enter your email address first.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    // Track forgot password click
    analytics.capture(AnalyticsEvents.FORGOT_PASSWORD_CLICKED);

    forgotPassword(
      {
        email: loginData.email,
        // Tell the backend where to send the member after clicking the reset link.
        // The backend appends ?code=uid@token to this URL.
        redirectUrl: window.location.origin + '/login',
      },
      {
        onSuccess: () => {
          // Anti-enumeration: same message regardless of whether the email exists.
          toast({
            title: "Check your inbox",
            description: "If an account exists with that email, you'll receive reset instructions.",
            status: "success",
            duration: 5000,
          });
        },
        onError: (error: any) => {
          toast({
            title: "Failed to send reset email",
            description: error?.response?.data?.detail || error?.response?.data?.message || error?.message || "An error occurred",
            status: "error",
            duration: 3000,
          });
        },
      }
    );
  };

  const handleResetPasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setResetData((prev) => ({ ...prev, [name]: value }));
  };

  const handleResetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetUid || !resetToken) return;

    if (!resetData.newPassword1) {
      toast({ title: "Password is required", status: "error", duration: 3000 });
      return;
    }
    if (resetData.newPassword1.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters.",
        status: "error",
        duration: 3000,
      });
      return;
    }
    if (resetData.newPassword1 !== resetData.newPassword2) {
      toast({ title: "Passwords do not match", status: "error", duration: 3000 });
      return;
    }

    resetPassword(
      {
        uid: resetUid,
        token: resetToken,
        newPassword1: resetData.newPassword1,
        newPassword2: resetData.newPassword2,
      },
      {
        onSuccess: () => {
          toast({
            title: "Password reset successfully",
            description: "You can now log in with your new password.",
            status: "success",
            duration: 4000,
          });
          // Clear the code param from the URL and show the login form
          navigate({ to: '/login', search: { form: 'login' } });
          setCurrentPage('login');
          setResetData({ newPassword1: '', newPassword2: '' });
        },
        onError: (error: any) => {
          const errorData = error?.response?.data;

          // Expired/invalid token is terminal for this form — surface the
          // server's own message, then send the member back to login once the
          // toast closes (the reset panel can do nothing useful from here).
          if (errorData?.token) {
            const msg = Array.isArray(errorData.token) ? errorData.token[0] : errorData.token;
            toast({
              title: "Reset link expired",
              description: msg || "This reset link has expired or is invalid. Please request a new one.",
              status: "error",
              duration: 5000,
              onCloseComplete: () => {
                navigate({ to: '/login', search: { form: 'login' } });
                setCurrentPage('login');
              },
            });
            setResetData({ newPassword1: '', newPassword2: '' });
            return;
          }

          let description = "An error occurred. The link may have expired.";
          if (errorData?.newPassword2) {
            const msgs = Array.isArray(errorData.newPassword2)
              ? errorData.newPassword2
              : [errorData.newPassword2];
            description = msgs.join(' ');
          } else if (errorData?.message) {
            description = errorData.message;
          } else if (errorData?.detail) {
            description = errorData.detail;
          }
          toast({
            title: "Password reset failed",
            description,
            status: "error",
            duration: 5000,
          });
          setResetData({ newPassword1: '', newPassword2: '' });
        },
      }
    );
  };

  //#region Page Components
  const resetPanel = () => (
    <VStack
      as="form"
      spacing={4}
      align="stretch"
      width="100%"
      maxWidth="600px"
      onSubmit={handleResetPassword}
    >
      <Button
        variant="ghost"
        color="white"
        className="back-button"
        mb={4}
        alignSelf="flex-start"
        pl={0}
        onClick={() => {
          navigate({ to: '/login', search: { form: 'login' } });
          setCurrentPage('login');
          setResetData({ newPassword1: '', newPassword2: '' });
          setShowResetPassword(false);
          setShowResetConfirmPassword(false);
        }}
      >
        <ArrowLeft />
        Back
      </Button>
      <Text fontSize="2xl" fontWeight="bold" mb={2}>
        Reset your password
      </Text>
      <Text color="gray.400" mb={2}>
        Enter a new password for your account.
      </Text>
      <FormControl isRequired>
        <InputGroup>
          <Input
            type={showResetPassword ? "text" : "password"}
            placeholder="New password"
            size="lg"
            name="newPassword1"
            value={resetData.newPassword1}
            onChange={handleResetPasswordInputChange}
          />
          <InputRightElement height="100%">
            <Button
              variant="ghost"
              aria-label={showResetPassword ? "Hide password" : "Show password"}
              onMouseDown={keepInputFocus}
              onClick={() => setShowResetPassword(!showResetPassword)}
              size="sm"
              padding={1}
            >
              {showResetPassword ? (
                <EyeOff color="white" />
              ) : (
                <Eye color="white" />
              )}
            </Button>
          </InputRightElement>
        </InputGroup>
      </FormControl>
      <FormControl isRequired>
        <InputGroup>
          <Input
            type={showResetConfirmPassword ? "text" : "password"}
            placeholder="Confirm new password"
            size="lg"
            name="newPassword2"
            value={resetData.newPassword2}
            onChange={handleResetPasswordInputChange}
          />
          <InputRightElement height="100%">
            <Button
              variant="ghost"
              aria-label={showResetConfirmPassword ? "Hide password" : "Show password"}
              onMouseDown={keepInputFocus}
              onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
              size="sm"
              padding={1}
            >
              {showResetConfirmPassword ? (
                <EyeOff color="white" />
              ) : (
                <Eye color="white" />
              )}
            </Button>
          </InputRightElement>
        </InputGroup>
      </FormControl>
      <Button
        bg="purple.500"
        color="white"
        _hover={{ bg: 'purple.600' }}
        type="submit"
        size="lg"
        isLoading={isResettingPassword}
      >
        Set New Password
      </Button>
    </VStack>
  );

  const registerPanel = () => (
    <VStack
      as="form"
      spacing={4}
      align="stretch"
      width="100%"
      maxWidth="600px"
      onSubmit={handleRegister}
      data-testid="signup-form"
    >
      <Button
        variant={'ghost'}
        color={'white'}
        _hover={{ bg: 'purple.50', color: 'purple.500' }}
        className={'back-button'}
        alignSelf="flex-start"
        pl={0}
        mb={4}
        onClick={handleBack}
      >
        <ArrowLeft />
        Back
      </Button>
      <HStack spacing={4}>
        <FormControl isRequired>
          <FormLabel>First name</FormLabel>
          <Input
            type="text"
            placeholder="First name"
            size="lg"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            data-testid="signup-first-name"
            autoComplete="given-name"
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Last name</FormLabel>
          <Input
            type="text"
            placeholder="Last name"
            size="lg"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            data-testid="signup-last-name"
            autoComplete="family-name"
          />
        </FormControl>
      </HStack>
      <FormControl isRequired>
        <FormLabel>Email</FormLabel>
        <Input
          type="email"
          placeholder="Email"
          size="lg"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          data-testid="signup-email"
          autoComplete="username"
        />
      </FormControl>
      <FormControl isRequired>
        <FormLabel>City, State</FormLabel>
        <Box position="relative" ref={locationFieldRef}>
          <Input
            type="text"
            placeholder="City, State (e.g. San Francisco, CA)"
            size="lg"
            value={locationSearchValue}
            onChange={handleLocationInputChange}
            onBlur={handleLocationInputBlur}
            maxLength={64}
            data-testid="signup-location"
            autoComplete="off"
          />
          {isLocationSuggestionsOpen && locationSuggestions.length > 0 && (
            <Box
              position="absolute"
              top="100%"
              left={0}
              right={0}
              bg="gray.800"
              borderRadius="md"
              mt={1}
              zIndex={10}
              maxH="200px"
              overflowY="auto"
              boxShadow="lg"
              data-testid="signup-location-suggestions"
            >
              {locationSuggestions.map((suggestion) => (
                <Box
                  key={suggestion.placeId}
                  p={3}
                  cursor="pointer"
                  _hover={{ bg: 'gray.700' }}
                  // onMouseDown (not onClick) + preventDefault so the input
                  // never blurs from this tap/click in the first place —
                  // selection can't race the blur-close timeout above.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleLocationSelect(suggestion);
                  }}
                >
                  <Text color="white">{suggestion.mainText}</Text>
                  <Text fontSize="sm" color="gray.400">
                    {suggestion.secondaryText}
                  </Text>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </FormControl>
      <FormControl isRequired>
        <FormLabel>Date of Birth</FormLabel>
        <Input
          type="date"
          size="lg"
          name="dob"
          value={formData.dob}
          onChange={handleInputChange}
          data-testid="signup-dob"
          autoComplete="bday"
          max={
            new Date(new Date().setFullYear(new Date().getFullYear() - 13))
              .toISOString()
              .split("T")[0]
          }
          sx={{
            '::-webkit-calendar-picker-indicator': {
              filter: 'invert(1)',
              cursor: 'pointer',
              opacity: '1 !important',
              display: 'block !important',
              width: '16px',
              height: '16px',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1
            },
            // Force visibility on all states
            '&:not(:focus)::-webkit-calendar-picker-indicator': {
              opacity: '1 !important',
              display: 'block !important'
            },
            '@media (max-width: 768px)': {
              '::-webkit-calendar-picker-indicator': {
                opacity: '1 !important',
                display: 'block !important',
                width: '20px',
                height: '20px',
                right: '10px'
              }
            }
          }}
        />
      </FormControl>
      <FormControl>
        <FormLabel>Bio</FormLabel>
        <Textarea
          placeholder="Tell us about yourself!"
          maxLength={255}
          size="lg"
          name="bio"
          value={formData.bio}
          onChange={handleInputChange}
          data-testid="signup-bio"
          autoComplete="off"
        />
      </FormControl>
      <FormControl>
        <Checkbox
          name="shareEmailOnProfile"
          isChecked={formData.shareEmailOnProfile}
          onChange={(e) => setFormData(prev => ({ ...prev, shareEmailOnProfile: e.target.checked }))}
          colorScheme="purple"
        >
          <Text fontSize="sm" color="white">Share email on profile</Text>
        </Checkbox>
      </FormControl>
      <FormControl isRequired>
        <FormLabel>Password</FormLabel>
        <InputGroup>
          <Input
            type={showPassword ? "text" : "password"}
            placeholder="Create a password"
            size="lg"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            data-testid="signup-password"
            autoComplete="new-password"
          />
          <InputRightElement height="100%">
            <Button
              variant="ghost"
              aria-label={showPassword ? "Hide password" : "Show password"}
              onMouseDown={keepInputFocus}
              onClick={() => setShowPassword(!showPassword)}
              size="sm"
              padding={1}
            >
              {showPassword ? (
                <EyeOff color="white" />
              ) : (
                <Eye color="white" />
              )}
            </Button>
          </InputRightElement>
        </InputGroup>
      </FormControl>
      <FormControl isRequired>
        <FormLabel>Confirm password</FormLabel>
        <InputGroup>
          <Input
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm password"
            size="lg"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            data-testid="signup-confirm-password"
            autoComplete="new-password"
          />
          <InputRightElement height="100%">
            <Button
              variant="ghost"
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              onMouseDown={keepInputFocus}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              size="sm"
              padding={1}
            >
              {showConfirmPassword ? (
                <EyeOff color="white" />
              ) : (
                <Eye color="white" />
              )}
            </Button>
          </InputRightElement>
        </InputGroup>
      </FormControl>
      <Text fontSize="xs" color="gray.400">
        By creating an account, you agree to the{" "}
        <Link color="blue.300">Terms of Service</Link> &{" "}
        <Link color="blue.300">Privacy Policy</Link>
      </Text>
      <Button bg="purple.500" color="white" _hover={{ bg: 'purple.600' }} type="submit" size="lg" isLoading={isRegistering} data-testid="signup-submit">
        Sign Up
      </Button>
    </VStack>
  );
  const loginPanel = () => (
    <VStack
      as="form"
      spacing={4}
      align="stretch"
      width="100%"
      maxWidth="600px"
      onSubmit={handleLogin}
      data-testid="login-form"
    >
      <Button
        variant="ghost"
        color={'white'}
        className={"back-button"}
        mb={4}
        alignSelf="flex-start"
        pl={0}
        onClick={handleBack}
      >
        <ArrowLeft />
        Back
      </Button>
      <FormControl isRequired>
        <Input
          type="email"
          placeholder="Email"
          size="lg"
          name="email"
          value={loginData.email}
          onChange={handleLoginInputChange}
          data-testid="login-email"
          autoComplete="username"
        />
      </FormControl>
      <FormControl isRequired>
        <InputGroup>
          <Input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            size="lg"
            name="password"
            value={loginData.password}
            onChange={handleLoginInputChange}
            data-testid="login-password"
            autoComplete="current-password"
          />
          <InputRightElement height="100%">
            <Button
              variant="ghost"
              color={'purple'}
              aria-label={showPassword ? "Hide password" : "Show password"}
              onMouseDown={keepInputFocus}
              onClick={() => setShowPassword(!showPassword)}
              size="sm"
              padding={1}
            >
              {showPassword ? (
                <EyeOff color="white" />
              ) : (
                <Eye color="white" />
              )}
            </Button>
          </InputRightElement>
        </InputGroup>
      </FormControl>
      <Button
        variant="link"
        color="blue.300"
        alignSelf="flex-end"
        onClick={handleForgotPassword}
        isLoading={isSendingReset}
        isDisabled={isSendingReset}
        fontWeight="normal"
      >
        Forgot Password?
      </Button>
      <Button
        bg="purple.500"
        color="white"
        _hover={{ bg: 'purple.600' }}
        type="submit"
        size="lg"
        onClick={handleLogin}
        data-testid="login-submit"
      >
        Log In
      </Button>
    </VStack>
  );
  //#endregion

  const handleIntroLogin = () => {
    setCurrentPage('login');
  };

  const handleIntroRegister = () => {
    setCurrentPage('register');
  };

  return (
    <Box className={"landing-background"} color="white" overflowY={'auto'}>
      <SEO
        title="Discover Your Next Favorite Restaurant"
        url={SITE_URL || undefined}
        jsonLd={LANDING_JSON_LD}
      />
      {currentPage === "intro" ? (
        <IntroCarousel onLogin={handleIntroLogin} onRegister={handleIntroRegister} />
      ) : (
        <Flex
          className={"landing-panel"}
          direction="column"
          align="center"
          justify="center"
          minHeight="100vh"
          p={8}
        >
          {currentPage === "reset" && resetPanel()}
          {currentPage === "register" && registerPanel()}
          {currentPage === "login" && loginPanel()}
        </Flex>
      )}
    </Box>
  );
};
export default Landing;